import { Controller } from "@hotwired/stimulus"
import TomSelect from "tom-select"

export default class extends Controller {
  static targets = ["workspace", "channel", "channelName", "channelLoading", "testBtn", "testResult", "legend"]
  static values = {
    selectedChannel: String,
    placeholder: { type: String, default: "Select a channel..." }
  }

  connect() {
    this.tomSelect = null
    this._fetched = false

    const details = this.element.closest("details")
    if (details && !details.open) {
      this._boundOnToggle = this._onDetailsToggle.bind(this)
      details.addEventListener("toggle", this._boundOnToggle, { once: true })
    } else {
      this._loadChannels()
    }
  }

  disconnect() {
    if (this.tomSelect) {
      this.tomSelect.destroy()
      this.tomSelect = null
    }
    if (this._boundOnToggle) {
      const details = this.element.closest("details")
      details?.removeEventListener("toggle", this._boundOnToggle)
      this._boundOnToggle = null
    }
  }

  _onDetailsToggle(event) {
    if (event.target.open) {
      this._loadChannels()
    }
  }

  _loadChannels() {
    if (this._fetched) return
    this._fetched = true
    if (this.hasWorkspaceTarget && this.workspaceTarget.value) {
      this.fetchChannels(this.workspaceTarget.value)
    }
  }

  onWorkspaceChange() {
    const workspaceId = this.workspaceTarget.value
    if (!workspaceId) {
      this.clearChannels()
      return
    }
    this.fetchChannels(workspaceId)
  }

  onChannelChange() {
    const value = this.tomSelect ? this.tomSelect.getValue() : this.channelTarget.value
    const option = this.tomSelect ? this.tomSelect.options[value] : null

    if (this.hasChannelNameTarget && option) {
      this.channelNameTarget.value = option.channelName || ""
    }
    this.hideTestResult()
  }

  async fetchChannels(integrationId) {
    this.showChannelLoading()
    const savedChannelId = this.selectedChannelValue || ""

    try {
      const response = await fetch(`/slack_integrations/${integrationId}/channels`, {
        headers: {
          "Accept": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        }
      })

      if (!response.ok) throw new Error("Failed to fetch channels")

      const channels = await response.json()
      this.populateChannels(channels, savedChannelId)
    } catch (error) {
      console.error("Failed to fetch Slack channels:", error)
      this.setPlainOptions([{ value: "", label: "Error loading channels" }])
    } finally {
      this.hideChannelLoading()
    }
  }

  populateChannels(channels, savedChannelId) {
    if (this.tomSelect) {
      this.tomSelect.destroy()
      this.tomSelect = null
    }

    this.channelTarget.innerHTML = ""

    const emptyOpt = document.createElement("option")
    emptyOpt.value = ""
    emptyOpt.textContent = this.placeholderValue
    this.channelTarget.appendChild(emptyOpt)

    channels.forEach(ch => {
      const opt = document.createElement("option")
      opt.value = ch.id
      opt.dataset.channelName = ch.name

      const prefix = ch.is_private ? "\uD83D\uDD12 " : "#"
      const memberLabel = ch.bot_is_member ? " \u2713" : ""
      opt.textContent = `${prefix}${ch.name}${memberLabel}`

      if (ch.id === savedChannelId) opt.selected = true
      this.channelTarget.appendChild(opt)
    })

    this.tomSelect = new TomSelect(this.channelTarget, {
      allowEmptyOption: true,
      placeholder: this.placeholderValue,
      sortField: { field: "text", direction: "asc" },
      onChange: () => this.onChannelChange(),
      render: {
        no_results: () => '<div class="no-results px-2 py-1 text-xs text-gray-500">No channels found</div>'
      }
    })

    if (savedChannelId) {
      this.tomSelect.setValue(savedChannelId, true)
    }

    this.updateLegend(channels)
  }

  clearChannels() {
    if (this.tomSelect) {
      this.tomSelect.destroy()
      this.tomSelect = null
    }
    this.channelTarget.innerHTML = '<option value="">Select a workspace first...</option>'
    if (this.hasChannelNameTarget) this.channelNameTarget.value = ""
    this.hideTestResult()
  }

  async testConnection() {
    const integrationId = this.workspaceTarget.value || this.workspaceTarget.dataset?.value
    const channelId = this.tomSelect ? this.tomSelect.getValue() : this.channelTarget.value

    if (!integrationId || !channelId) {
      this.showTestResult("Select a workspace and channel first.", "text-yellow-600")
      return
    }

    this.testBtnTarget.disabled = true
    this.testBtnTarget.textContent = "Testing..."
    this.hideTestResult()

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      const response = await fetch(`/slack_integrations/${integrationId}/test_channel`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": csrfToken,
          "X-Requested-With": "XMLHttpRequest"
        },
        body: JSON.stringify({ channel_id: channelId })
      })

      const result = await response.json()

      if (result.success) {
        this.showTestResult(result.message, "text-green-600")
        this.selectedChannelValue = channelId
        this.fetchChannels(integrationId)
      } else if (result.needs_invite) {
        this.showTestResult(result.error, "text-yellow-600")
      } else {
        this.showTestResult(result.error || "Test failed", "text-red-600")
      }
    } catch (error) {
      this.showTestResult("Connection test failed. Please try again.", "text-red-600")
    } finally {
      this.testBtnTarget.disabled = false
      this.testBtnTarget.innerHTML = '<svg class="w-3 h-3 mr-1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon></svg>Test Connection'
    }
  }

  async sendTestRecap(event) {
    const btn = event.currentTarget
    const endpointId = btn.dataset.endpointId
    const scope = endpointId ? "endpoint" : "account"

    btn.disabled = true
    const origHTML = btn.innerHTML
    btn.textContent = "Sending..."

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      const body = { scope }
      if (endpointId) body.endpoint_id = endpointId

      const response = await fetch("/slack_integrations/test_recap", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": csrfToken,
          "X-Requested-With": "XMLHttpRequest"
        },
        body: JSON.stringify(body)
      })

      const result = await response.json()
      if (result.success) {
        this.showTestResult(result.message, "text-green-600")
      } else {
        this.showTestResult(result.error || "Failed to send test recap", "text-red-600")
      }
    } catch (error) {
      this.showTestResult("Failed to send test recap. Please try again.", "text-red-600")
    } finally {
      btn.disabled = false
      btn.innerHTML = origHTML
    }
  }

  updateLegend(channels) {
    if (!this.hasLegendTarget) return
    const hasMember = channels.some(ch => ch.bot_is_member)
    const hasPrivate = channels.some(ch => ch.is_private)

    let parts = []
    if (hasMember) parts.push("\u2713 = bot is a member")
    if (hasPrivate) parts.push("\uD83D\uDD12 = private channel")
    this.legendTarget.textContent = parts.join("  ·  ")
    this.legendTarget.classList.toggle("hidden", parts.length === 0)
  }

  setPlainOptions(options) {
    if (this.tomSelect) {
      this.tomSelect.destroy()
      this.tomSelect = null
    }
    this.channelTarget.innerHTML = options.map(o =>
      `<option value="${o.value}">${o.label}</option>`
    ).join("")
  }

  showTestResult(message, colorClass) {
    this.testResultTarget.textContent = message
    this.testResultTarget.className = `text-xs ${colorClass}`
    this.testResultTarget.classList.remove("hidden")
  }

  hideTestResult() {
    if (this.hasTestResultTarget) {
      this.testResultTarget.classList.add("hidden")
    }
  }

  showChannelLoading() {
    if (this.hasChannelLoadingTarget) {
      this.channelLoadingTarget.classList.remove("hidden")
    }
  }

  hideChannelLoading() {
    if (this.hasChannelLoadingTarget) {
      this.channelLoadingTarget.classList.add("hidden")
    }
  }
}
